import type { Definition } from "@/lib/search";

import type { CardSignal } from "./cardSignal";
import { resultBadges, type Badge } from "./resultBadges";

const TONE: Record<Badge["tone"], string> = {
  neutral: "border-line bg-void text-ink-2",
  known: "border-line bg-line-soft text-ink-3",
  // The known family with no fill — the badge equivalent of dashing the underline in the known
  // colour rather than introducing a hue for a secondary signal.
  elsewhere: "border-line bg-void text-ink-3",
  gap: "border-accent-border bg-accent-tint text-ink-3",
};

function BadgeRow({ badges }: { badges: Badge[] }) {
  return (
    <>
      {badges.map((badge, i) => (
        <span
          key={i}
          className={[
            "rounded-[var(--r-2)] border px-[7px] py-[3px] text-[14px]/[1.3] font-semibold",
            badge.mono ? "font-mono" : "",
            TONE[badge.tone],
          ].join(" ")}
        >
          {badge.label}
        </span>
      ))}
    </>
  );
}

interface ActionButtonProps {
  carded: boolean;
  /** This entry is already one of the definitions on that card. */
  onCard: boolean;
  showShortcut: boolean;
  onAdd: () => void;
  onEdit: () => void;
  busy?: boolean;
}

function ActionButton({ carded, onCard, showShortcut, onAdd, onEdit, busy }: ActionButtonProps) {
  // A card exists for this word, but not this definition. Adding appends to it rather than
  // being refused — which is the whole point: every entry written this way shares one card, so
  // before this the first one added made the rest unreachable.
  if (carded && !onCard) {
    return (
      <button
        type="button"
        onClick={onAdd}
        disabled={busy}
        className="shrink-0 rounded-[var(--r-2)] border border-accent-line bg-void px-3 py-2 text-[14px]/[1] font-semibold text-accent disabled:opacity-60"
      >
        {busy ? "追加中…" : "+ カードに追加"}
        {showShortcut && !busy && (
          <span className="ml-[7px] font-mono text-[14px]/[1] opacity-75">A</span>
        )}
      </button>
    );
  }
  if (carded) {
    return (
      <button
        type="button"
        onClick={onEdit}
        className="shrink-0 rounded-[var(--r-2)] border border-line-strong bg-line-soft px-3 py-2 text-[14px]/[1] font-semibold text-ink-3"
      >
        {onCard ? "✓ カードに収録済" : "✓ 収録済"}
        {showShortcut && (
          <span className="ml-[7px] font-mono text-[14px]/[1] opacity-60">E</span>
        )}
      </button>
    );
  }
  return (
    <button
      type="button"
      onClick={onAdd}
      disabled={busy}
      className="shrink-0 rounded-[var(--r-2)] bg-accent px-3 py-2 text-[14px]/[1] font-semibold text-white disabled:opacity-60"
    >
      {busy ? "追加中…" : "+ 追加"}
      {showShortcut && !busy && (
        <span className="ml-[7px] font-mono text-[14px]/[1] opacity-75">A</span>
      )}
    </button>
  );
}

export interface ResultCardProps {
  entry: Definition;
  /**
   * The row's position in the result list, as a check hook (`result.0`, `result.1`, …).
   *
   * The browser checks need to name a row and to ask which one is focused. They used to do it by
   * sniffing the focused row's Tailwind class, which is not a contract — it went silently empty the
   * first time the focus ring's class list changed, and reported the assertion as a failure of the
   * key it was testing rather than of the selector.
   */
  index?: number;
  /**
   * Render without the frame, because a wrapper outside is drawing it.
   *
   * 日本-498 puts every result row in a box that carries the 26px kind gutter, so the border, the
   * radius and the right margin belong to that box. Left on here as well, the row painted a second
   * rounded rectangle inside the first, inset by the gutter's width.
   */
  bare?: boolean;
  /** What is known about `entry.term`. Only `known` counts as carded for the button. */
  signal: CardSignal;
  /** This entry is one of the definitions already on that card. */
  onCard?: boolean;
  focused: boolean;
  /** The density rule: at five or more entries every result is one line, and the focused one expands. */
  collapsed: boolean;
  /** Suppresses the `A` / `E` shortcut glyphs: naming a key a phone does not have is a lie. */
  isPhone?: boolean;
  busy?: boolean;
  onFocus: () => void;
  onAdd: () => void;
  onEdit: () => void;
  /** The card's right-click menu — built by `Read`, which owns the actions. */
  onContextMenu?: (event: React.MouseEvent) => void;
}

export function ResultCard({
  entry,
  index,
  bare = false,
  signal,
  onCard = false,
  focused,
  collapsed,
  isPhone = false,
  busy,
  onFocus,
  onAdd,
  onEdit,
  onContextMenu,
}: ResultCardProps) {
  // **Only `known` is carded.** A word you hold under another spelling reaches the plain `+ add`
  // branch, which is the honest label: pressing it makes a new card rather than joining one. That
  // is the rule (Vellum 日本-271) and it is what corrected the ~168 rows that used to read
  // `+ カードに追加` — the label meaning *joins your existing card* — while silently creating one.
  const carded = signal.state === "known";
  const badges = resultBadges(entry, signal);
  const denseBadges = badges.filter((b) => b.dense || b.tone !== "neutral");

  if (collapsed && !focused) {
    const firstGloss = entry.senses[0]?.glosses.join("; ") ?? "";
    return (
      <button
        type="button"
        onClick={onFocus}
        onContextMenu={onContextMenu}
        data-check={index === undefined ? undefined : `result.${index}`}
        data-focused={focused ? "1" : undefined}
        // **`bare` has to be honoured HERE too**, and this branch returning early is what hid it
        // (日本-498, reported by Alex): the collapsed row kept its own frame *and* its own width.
        // Inside the kind gutter's wrapper that drew a second rounded box, inset by the gutter,
        // and — because a flex item with no `flex-1` sizes to its content — one that stopped
        // partway across the row. Every expanded row was correct, which is what made it read as a
        // formatting glitch rather than as one branch missing a prop.
        className={[
          "bg-panel px-[15px] py-[11px] text-left",
          // The phone stacks — see the badge-row note below. Everywhere else this is the one
          // baseline-aligned line it has always been.
          isPhone ? "flex flex-col gap-[7px]" : "flex items-center justify-between gap-3",
          bare
            ? "min-w-0 flex-1"
            : "mr-3 rounded-[var(--r-3)] border border-line",
        ].join(" ")}
      >
        <span
          className={[
            "flex min-w-0 items-baseline gap-[10px]",
            isPhone ? "w-full justify-between" : "flex-wrap",
          ].join(" ")}
        >
          {/*
            **The headword and its reading are ONE flex item on the phone, not two.** They sit
            beside each other everywhere else in the app, and under the `justify-between` that
            puts `+ 追加` on the right they would otherwise be spread to three separate positions
            — `１人 … ひとり … + 追加`, which reads as a layout fault rather than as a heading.
            Grouping them is what makes the button the only thing pushed away.
          */}
          <span className="flex min-w-0 flex-wrap items-baseline gap-[10px]">
            {/*
              `flex-wrap` restores what the markup this replaced had, and it is a GUARD rather
              than a fix for anything observed. Nothing inside the group can shrink — `min-w-0`
              lets the group give way and its two spans would then paint out of it, over the label
              `justify-between` has pushed right, clipped rather than scrolled by `ResultList`'s
              `overflow-hidden`. Measured at 390 over ~440 collapsed rows, the tightest
              headword-to-button gap is **128px** (ニワトリ), so no real dictionary headword comes
              close; a collapsed list is long, and the long headwords turn up in short lists that
              never collapse. Wrapping costs nothing and fails softly if that ever stops holding.
            */}
            <span className="font-jp text-[24px]/[1.1] font-semibold">{entry.term}</span>
            <span className="font-jp text-[14px]/[1] text-ink-3">{entry.primaryReading}</span>
          </span>
          {/*
            **The collapsed row keeps every badge that names a sorting tag** — the rank, the
            markers and JLPT (日本-499). It used to keep `b.mono || b.tone !== "neutral"`, which
            asked what a badge looks like in order to answer what it means: the markers are neutral
            and not mono, so that test would have dropped them from precisely the rows this exists
            for. `常用` still goes, because it names no tag.

            This is where the ticket is most visible: collapsed, 直ぐ used to read `#71,738` — near
            the bottom of a 100k list — beside a card that actually sorts at 10,000.
          */}
          {!isPhone && <BadgeRow badges={denseBadges} />}
          {!isPhone && (
            <span className="truncate text-[15px]/[1.4] text-ink-2">
              {firstGloss}
              {entry.senses.length > 1 && (
                <span className="text-ink-4"> · {entry.senses.length}語義</span>
              )}
            </span>
          )}
          {isPhone && (
            <span className="shrink-0 rounded-[var(--r-2)] border border-line-strong bg-void px-[11px] py-[7px] text-[14px]/[1] font-semibold text-ink-2">
              {carded && onCard ? "✓" : carded ? "+ カードに追加" : "+ 追加"}
            </span>
          )}
        </span>
        {isPhone && (
          <>
            <span className="flex flex-wrap items-center gap-2">
              <BadgeRow badges={denseBadges} />
            </span>
            <span className="w-full truncate text-[15px]/[1.4] text-ink-2">
              {firstGloss}
              {entry.senses.length > 1 && (
                <span className="text-ink-4"> · {entry.senses.length}語義</span>
              )}
            </span>
          </>
        )}
        {!isPhone && (
          <span className="shrink-0 rounded-[var(--r-2)] border border-line-strong bg-void px-[11px] py-[7px] text-[14px]/[1] font-semibold text-ink-2">
            {carded && onCard ? "✓" : carded ? "+ カードに追加" : "+ 追加"}
          </span>
        )}
      </button>
    );
  }

  return (
    <div
      onMouseEnter={onFocus}
      onContextMenu={onContextMenu}
      data-check={index === undefined ? undefined : `result.${index}`}
      data-focused={focused ? "1" : undefined}
      className={[
        "flex flex-col gap-2 bg-panel px-4",
        // `bare` drops the frame, because something outside is drawing it. 日本-498 puts every row
        // in a wrapper that carries the kind gutter, and the border and radius have to live on THAT
        // — nested, the row painted a second rounded box inside the first, 26px off centre. The
        // padding stays here: it is the card's own, and the gutter is flush to the edge.
        bare ? "min-w-0 flex-1" : "mr-3 rounded-[var(--r-3)]",
        bare
          ? focused
            ? "py-[13px]"
            : "py-3"
          : focused
            ? "border border-accent-line py-[13px] shadow-[var(--sh-frame)]"
            : "border border-line py-3",
      ].join(" ")}
    >
      {/*
        **On a phone the badges take their own line** (日本-499). Sharing the headword's line means
        sharing it with the action button, which measured leaves the badge row **210px** — and 一人
        already wrapped to THREE badge lines there on four badges, before the markers existed.

        Measured at 390 after: the row is 318px and the card's own `px-4` takes 32 of it, so the
        badges get **286px**. 一人's five now come to 353px at this gap, so they take **two** lines
        — one fewer than before, while carrying one badge more. One line is not reachable at this
        width and was not worth buying: getting there means dropping the rank or shrinking the
        badge, and the rank is the evidence the markers qualify.

        The desktop keeps one baseline-aligned row, where the headword, the reading and the badges
        sit together: it has the room (395px used of ~1011 at 1147), and splitting it would cost a
        line for nothing.
      */}
      <div className="flex items-start justify-between gap-3">
        <div className="flex flex-wrap items-baseline gap-[10px]">
          <span
            className={[
              "font-jp font-semibold",
              focused ? "text-[27px]/[1.1]" : "text-[25px]/[1.1]",
            ].join(" ")}
          >
            {entry.term}
          </span>
          <span
            className={[
              "font-jp text-ink-3",
              focused ? "text-[15px]/[1]" : "text-[14px]/[1]",
            ].join(" ")}
          >
            {entry.primaryReading}
          </span>
          {!isPhone && <BadgeRow badges={badges} />}
        </div>
        <ActionButton
          carded={carded}
          onCard={onCard}
          showShortcut={focused && !isPhone}
          onAdd={onAdd}
          onEdit={onEdit}
          busy={busy}
        />
      </div>
      {isPhone && (
        <div className="flex flex-wrap items-center gap-2">
          <BadgeRow badges={badges} />
        </div>
      )}

      {/* All senses, always. A 17-sense する simply runs long and the list scrolls. */}
      {entry.senses.map((sense, i) => (
        <div key={i} className="flex gap-[9px] text-body text-ink">
          <span className="shrink-0 font-mono text-[14px]/[1.8] font-semibold text-ink-4">
            {i + 1}
            {sense.partsOfSpeech.length > 0 && ` ${abbreviate(sense.partsOfSpeech)}`}
          </span>
          <span>
            {sense.glosses.join("; ")}
            {sense.misc.length > 0 && <span className="text-ink-3"> ({sense.misc.join(", ")})</span>}
            {sense.info.length > 0 && <span className="text-ink-3"> ({sense.info.join(", ")})</span>}
          </span>
        </div>
      ))}

      {/*
        The rows are NOT collapsed into one — each definition stays selectable, because which one
        you press decides which leads the card. But every one of them writes the same Japanese
        field, so pressing any of them produces one card carrying all of them, and a row that did
        not say so would look like a choice between definitions rather than an ordering of them.
      */}
      {entry.mergesWith > 0 && (
        <p className="text-[14px]/[1.5] text-ink-3">
          {carded && !onCard
            ? `この語義を${entry.term}のカードに追加します。`
            : `他${entry.mergesWith}件の${entry.term}の定義と統合します。これを先頭に。`}
        </p>
      )}
      {entry.isArchaic && (
        <p className="text-[14px]/[1.5] text-ink-3">
          古語 — 自分で追加しない限り統合されません。
        </p>
      )}
    </div>
  );
}

/**
 * The mocks show a compact mono POS prefix ("v1,vi"), but the engine stores Wacton's long
 * descriptions ("Verb (ichidan)") because that is what is written into cards. Shortening for display
 * only keeps both true.
 */
function abbreviate(partsOfSpeech: readonly string[]): string {
  return partsOfSpeech
    .map((pos) =>
      pos
        .replace(/^Noun \(common\)$/, "n")
        .replace(/^Noun.*/, "n")
        .replace(/^Verb \(ichidan\)$/, "v1")
        .replace(/^Verb \(godan.*/, "v5")
        .replace(/^Verb \(transitive\)$/, "vt")
        .replace(/^Verb \(intransitive\)$/, "vi")
        .replace(/^Verb.*/, "v")
        .replace(/^Adjective \(i\)$/, "adj-i")
        .replace(/^Adjective \(na\)$/, "adj-na")
        .replace(/^Adjective.*/, "adj")
        .replace(/^Adverb.*/, "adv")
        .replace(/^Expression.*/, "exp")
        .replace(/^Particle.*/, "prt"),
    )
    .filter((v, i, all) => all.indexOf(v) === i)
    .join(",");
}
